import { type Editor, useEditorState } from '@tiptap/react';
import { ImagePlusIcon } from 'lucide-react';
import { useCallback } from 'react';

import { Button } from '@/components/ui/button';
import { useTiptapEditor } from '@/hooks/tiptap';
import { shouldShow } from '@/lib/tiptap';
import { isNodeInSchema } from '@/lib/tiptap/tiptapUtils';

import type { SelectMediaButtonProps } from '.';

export function useIsMediaActive(editor: Editor | null, extensionName: string): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => ctx.editor?.isActive(extensionName),
    }) ?? false
  );
}

function useSelectMediaButton(editor: Editor | null, disabled: boolean = false) {
  const isActive: boolean = useIsMediaActive(editor, 'select-media');
  const onInsertMediaSelector = useCallback(() => {
    if (disabled) return false;
    if (!editor) return false;

    return editor
      .chain()
      .focus()
      .insertContent({
        type: 'select-media',
      })
      .run();
  }, [editor, disabled]);

  return {
    isActive,
    onInsertMediaSelector,
  };
}

function useSelectMediaButtonClick(onClick: SelectMediaButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function SelectMediaButton(selectMediaButtonProps: SelectMediaButtonProps) {
  const { editor: providedEditor, disabled, onClick, children, ...props } = selectMediaButtonProps;

  const editor: Editor | null = useTiptapEditor(providedEditor);
  const inSchema: boolean = isNodeInSchema('select-media', editor);

  const { isActive, onInsertMediaSelector } = useSelectMediaButton(editor, disabled);

  const handleClick = useSelectMediaButtonClick(onClick, () => {
    onInsertMediaSelector();
  });

  const show: boolean = shouldShow([!!editor, inSchema]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <Button type="button" onClick={handleClick} disabled={disabled} data-active-state={isActive ? 'on' : 'off'} aria-label="Add image" aria-pressed={isActive} {...props}>
      {children || <ImagePlusIcon />}
    </Button>
  );
}
